import React from 'react';
import { Award, MapPin, Briefcase, GraduationCap } from 'lucide-react';
import { personalInfo, images, awards } from '../data/mockData';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#0f1419] pt-20">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#d4af37]/20 to-transparent rounded-2xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={images.casual}
                  alt="Jerry Fisayo-Bambi"
                  className="w-full h-[600px] object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -right-6 bg-[#1a1f2e] p-6 rounded-xl border border-[#2a3441] shadow-2xl">
                <div className="flex items-center space-x-3">
                  <MapPin className="text-[#d4af37]" size={24} />
                  <div>
                    <p className="text-[#f5f5f0] font-medium">Based in</p>
                    <p className="text-[#a0a0a0] text-sm">{personalInfo.location}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8 order-1 lg:order-2">
              <div>
                <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-4">
                  About Me
                </p>
                <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#f5f5f0] leading-tight mb-6">
                  Telling Africa's Stories to the World
                </h1>
              </div>
              
              <div className="space-y-6 text-[#a0a0a0] leading-relaxed text-lg">
                {personalInfo.fullBio.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="py-24 bg-[#0a0d10]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
              Career Journey
            </p>
            <h2 className="font-serif text-4xl font-bold text-[#f5f5f0]">
              Professional Experience
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                role: 'Senior Journalist',
                company: 'Euronews',
                period: 'Present',
                description: 'Covering African stories for global audiences, hosting Inspire Africa, and producing in-depth features on continental developments.'
              },
              {
                role: 'Host - Inspire Africa',
                company: 'Euronews',
                period: 'Present',
                description: 'Leading flagship program showcasing African innovation, leadership, and success stories to international viewers.'
              },
              {
                role: 'International Event Moderator',
                company: 'Global Conferences',
                period: 'Ongoing',
                description: 'Moderating high-profile panels at international summits including Meta, African Union, and global leadership forums.'
              },
              {
                role: 'Communication Strategist',
                company: 'Consulting',
                period: 'Ongoing',
                description: 'Advising organizations on media strategy, brand storytelling, and effective communication campaigns.'
              }
            ].map((exp, index) => (
              <div
                key={index}
                className="group p-8 bg-[#1a1f2e] rounded-xl border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="text-[#d4af37]" size={24} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3 flex-wrap gap-2">
                      <h3 className="font-serif text-xl font-semibold text-[#f5f5f0]">
                        {exp.role}
                      </h3>
                      <span className="px-3 py-1 bg-[#d4af37]/10 text-[#d4af37] text-xs font-medium rounded-full">
                        {exp.period}
                      </span>
                    </div>
                    <p className="text-[#d4af37] text-sm font-medium">{exp.company}</p>
                    <p className="text-[#a0a0a0] leading-relaxed">
                      {exp.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
              Recognition
            </p>
            <h2 className="font-serif text-4xl font-bold text-[#f5f5f0]">
              Awards & Honours
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {awards.map((award) => (
              <div
                key={award.id}
                className={`group p-6 rounded-xl border transition-all duration-300 ${
                  award.highlight
                    ? 'bg-gradient-to-br from-[#d4af37]/10 to-[#1a1f2e] border-[#d4af37]/30 hover:border-[#d4af37]'
                    : 'bg-[#1a1f2e] border-[#2a3441] hover:border-[#d4af37]/50'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    award.highlight ? 'bg-[#d4af37] text-[#0f1419]' : 'bg-[#d4af37]/10 text-[#d4af37]'
                  }`}>
                    <Award size={20} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        award.highlight
                          ? 'bg-[#d4af37] text-[#0f1419]'
                          : 'bg-[#d4af37]/10 text-[#d4af37]'
                      }`}>
                        {award.year}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-[#f5f5f0]">
                      {award.title}
                    </h3>
                    <p className="text-[#a0a0a0] text-sm leading-relaxed">
                      {award.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-[#0a0d10]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
              In Action
            </p>
            <h2 className="font-serif text-4xl font-bold text-[#f5f5f0]">
              Gallery
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { src: images.broadcasting, alt: 'Broadcasting' },
              { src: images.moderating, alt: 'Event moderation' },
              { src: images.gallery1, alt: 'On assignment' },
              { src: images.hero, alt: 'Professional portrait with champagne' },
              { src: images.gallery2, alt: 'Studio portrait' },
              { src: images.speaking, alt: 'Public speaking' },
              { src: images.gallery3, alt: 'Media event' },
              { src: images.casual, alt: 'Casual portrait' }
            ].map((image, index) => (
              <div
                key={index}
                className={`relative rounded-xl overflow-hidden group ${
                  index === 3 || index === 7 ? 'row-span-2' : ''
                }`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={{ minHeight: index === 3 || index === 7 ? '400px' : '200px' }}
                />
                <div className="absolute inset-0 bg-[#0f1419]/0 group-hover:bg-[#0f1419]/40 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;